import { format, addDays, addWeeks, addMonths, addYears, parseISO, startOfWeek } from "date-fns";
import { RecurrenceConfig } from "@/components/admin/facility_schedule/modal/recurrence-selector";

export interface TimeSlot {
  start_time: string;
  end_time: string;
}

/**
 * Splits a time range into multiple slots based on duration and break.
 * Handles overnight slots (end_time <= start_time).
 */
export const splitTimeRangeIntoSlots = (
  startTime: string,
  endTime: string,
  durationHours: number,
  breakMinutes: number = 0
): TimeSlot[] => {
  const slots: TimeSlot[] = [];

  const [startH, startM] = startTime.split(":").map(Number);
  const [endH, endM] = endTime.split(":").map(Number);

  let currentMinutes = startH * 60 + startM;
  let totalEndMinutes = endH * 60 + endM;

  // Overnight handling
  if (totalEndMinutes <= currentMinutes) {
    totalEndMinutes += 24 * 60;
  }

  const slotDurationMinutes = durationHours * 60;

  while (currentMinutes + slotDurationMinutes <= totalEndMinutes) {
    const slotStartMin = currentMinutes % (24 * 60);
    const slotEndMin = (currentMinutes + slotDurationMinutes) % (24 * 60);

    const startStr = `${String(Math.floor(slotStartMin / 60)).padStart(2, "0")}:${String(slotStartMin % 60).padStart(2, "0")}`;
    const endStr = `${String(Math.floor(slotEndMin / 60)).padStart(2, "0")}:${String(slotEndMin % 60).padStart(2, "0")}`;

    slots.push({ start_time: startStr, end_time: endStr });

    currentMinutes += slotDurationMinutes + breakMinutes;
  }

  return slots;
};

export const normalizeWeekday = (day: string): number | undefined => {
  const normalized = day.trim().toLowerCase();
  if (normalized.startsWith("su")) return 0;
  if (normalized.startsWith("mo")) return 1;
  if (normalized.startsWith("tu")) return 2;
  if (normalized.startsWith("we")) return 3;
  if (normalized.startsWith("th")) return 4;
  if (normalized.startsWith("fr")) return 5;
  if (normalized.startsWith("sa")) return 6;
  return undefined;
};

const getNthWeekdayOfMonth = (
  year: number,
  month: number,
  weekday: number,
  occurrence: number
): Date => {
  if (occurrence === -1) {
    const lastDay = new Date(year, month + 1, 0);
    const diff = (lastDay.getDay() - weekday + 7) % 7;
    return new Date(year, month + 1, lastDay.getDate() - diff);
  }

  const firstDay = new Date(year, month, 1);
  const offset = (weekday - firstDay.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + 7 * (occurrence - 1));
};

export const deriveEndDateFromCount = (
  config: RecurrenceConfig,
  startDate: string
): string | undefined => {
  if (config.endCondition !== "after" || !config.occurrences || config.occurrences < 1) {
    return undefined;
  }

  const start = parseISO(startDate);
  if (Number.isNaN(start.getTime())) return undefined;

  const effectiveInterval = Math.max(1, config.interval);
  const count = config.occurrences;

  if (config.frequency === "daily") {
    return format(addDays(start, (count - 1) * effectiveInterval), "yyyy-MM-dd");
  }

  if (config.frequency === "weekly") {
    if (config.weekDays && config.weekDays.length > 0) {
      const weekdays = config.weekDays
        .map(normalizeWeekday)
        .filter((day): day is number => day !== undefined)
        .map((day) => (day === 0 ? 7 : day))
        .sort((a, b) => a - b);

      if (weekdays.length === 0) {
        return format(addWeeks(start, (count - 1) * effectiveInterval), "yyyy-MM-dd");
      }

      let occurrences = 0;
      let weekStart = startOfWeek(start, { weekStartsOn: 1 });
      let lastDate = start;

      while (occurrences < count) {
        for (const weekday of weekdays) {
           const candidate = addDays(weekStart, weekday - 1);
          if (candidate < start) continue;
          if (occurrences < count) {
            lastDate = candidate;
            occurrences += 1;
          } else {
            break;
          }
        }
        weekStart = addWeeks(weekStart, effectiveInterval);
      }

      return format(lastDate, "yyyy-MM-dd");
    }

    return format(addWeeks(start, (count - 1) * effectiveInterval), "yyyy-MM-dd");
  }

  if (config.frequency === "monthly") {
    if (config.monthlyType === "day") {
      let occurrences = 0;
      let current = start;
      let lastDate = start;
      const targetDay = config.monthlyDay;

      while (occurrences < count) {
        const candidate = new Date(current.getFullYear(), current.getMonth(), targetDay);
        if (candidate >= start) {
          lastDate = candidate;
          occurrences += 1;
        }
        current = addMonths(current, effectiveInterval);
      }

      return format(lastDate, "yyyy-MM-dd");
    }

    let occurrences = 0;
    let current = start;
    let lastDate = start;

    while (occurrences < count) {
      const month = current.getMonth();
      const year = current.getFullYear();
      const weekday = normalizeWeekday(config.monthlyPattern.day) ?? current.getDay();
      const occurrenceIndex =
        config.monthlyPattern.week === "last"
          ? -1
          : ["first", "second", "third", "fourth"].indexOf(config.monthlyPattern.week) + 1;
      const candidate = getNthWeekdayOfMonth(year, month, weekday, occurrenceIndex);
      if (candidate >= start) {
        lastDate = candidate;
        occurrences += 1;
      }
      current = addMonths(current, effectiveInterval);
    }

    return format(lastDate, "yyyy-MM-dd");
  }

  if (config.frequency === "yearly") {
    return format(addYears(start, (count - 1) * effectiveInterval), "yyyy-MM-dd");
  }

  return undefined;
};

export const generateRRuleString = (config: RecurrenceConfig, startDate: string): string => {
  if (!config.frequency) return "";

  const parts: string[] = [];
  parts.push(`FREQ=${config.frequency.toUpperCase()}`);

  const effectiveInterval = Math.max(1, config.interval);
  parts.push(`INTERVAL=${effectiveInterval}`);

  if (config.frequency === "weekly" && config.weekDays.length > 0) {
    const dayMap: Record<string, string> = {
      Mon: "MO", Tue: "TU", Wed: "WE", Thu: "TH", Fri: "FR", Sat: "SA", Sun: "SU",
    };
    const days = config.weekDays.map((d) => dayMap[d]).join(",");
    parts.push(`BYDAY=${days}`);
  }

  if (config.frequency === "monthly") {
    if (config.monthlyType === "day") {
      parts.push(`BYMONTHDAY=${config.monthlyDay}`);
    } else {
      const posMap: Record<string, string> = {
        first: "1", second: "2", third: "3", fourth: "4", last: "-1",
      };
      const dayMap: Record<string, string> = {
        Monday: "MO", Tuesday: "TU", Wednesday: "WE", Thursday: "TH",
        Friday: "FR", Saturday: "SA", Sunday: "SU",
      };
      const pos = posMap[config.monthlyPattern.week];
      const day = dayMap[config.monthlyPattern.day];
      parts.push(`BYDAY=${pos}${day}`);
    }
  }

  if (config.endCondition === "after") {
    parts.push(`COUNT=${config.occurrences}`);
  } else if (config.endCondition === "on_date" && config.endDate) {

    const untilDate = config.endDate.replace(/-/g, "") + "T000000Z";
    parts.push(`UNTIL=${untilDate}`);
  }


  return "RRULE:" + parts.join(";");
};

export const buildRRule = (data: any): string => {
  if (!data.frequency) return "";

  const parts: string[] = [];

  parts.push(`FREQ=${data.frequency.toUpperCase()}`);

  const effectiveInterval = Math.max(1, data.interval ?? 1);
  parts.push(`INTERVAL=${effectiveInterval}`);

  if (data.frequency === "weekly" && data.weekDays?.length) {
    const dayMap: Record<string, string> = {
      Mon: "MO", Tue: "TU", Wed: "WE", Thu: "TH", Fri: "FR", Sat: "SA", Sun: "SU",
    };
    parts.push(`BYDAY=${data.weekDays.map((d: string) => dayMap[d]).join(",")}`);
  }

  if (data.end_date) {
    const until = data.end_date.replace(/-/g, "") + "T000000Z";
    parts.push(`UNTIL=${until}`);
  }

  return `RRULE:${parts.join(";")}`;
};

/**
 * Parses an RRule string into a preview-friendly format or back to config if needed.
 * Currently just basic parsing for display.
 */
export const getRRuleDescription = (rrule: string): string => {
  if (!rrule) return "No recurrence";
  return rrule;
};